import { Link } from 'react-router-dom';

export default function Landing() {
  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      {/* Navigation */}
      <nav className="bg-gray-900 border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center gap-2">
              <span className="text-2xl leading-none">👶</span>
              <span className="font-bold text-xl text-primary-500 tracking-tight">myTotTot</span>
            </div>
            <div className="flex items-center space-x-6">
              <Link
                to="/emergency"
                className="text-sm font-medium text-red-400 hover:text-red-300 transition-colors flex items-center gap-1"
                title="Emergency medical access for authorized contacts"
              >
                <span className="text-xs">🚨</span>
                Emergency
              </Link>
              <Link
                to="/login"
                className="text-sm font-medium text-gray-300 hover:text-white transition-colors"
              >
                Log in
              </Link>
              <Link
                to="/register"
                className="bg-primary-600 hover:bg-primary-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-lg shadow-primary-900/50"
              >
                Sign Up Free
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-12 lg:gap-8 pt-10 pb-16 sm:pt-16 sm:pb-24 lg:pt-20 lg:pb-32">
            <div className="sm:text-center md:max-w-2xl md:mx-auto lg:col-span-6 lg:text-left flex flex-col justify-center">
              <h1 className="text-4xl tracking-tight font-extrabold text-white sm:text-5xl md:text-6xl">
                <span className="block">From pregnancy to preschool,</span>
                <span className="block text-primary-500">your data stays yours.</span>
              </h1>
              <p className="mt-3 text-base text-gray-400 sm:mt-5 sm:text-xl lg:text-lg xl:text-xl">
                The world's first comprehensive pregnancy &amp; baby tracker with{' '}
                <strong>Zero-Knowledge Encryption</strong>. Track symptoms, kicks, feeds, sleep, and
                health—but we literally cannot read your data. Only you hold the keys.
              </p>
              <div className="mt-8 sm:max-w-lg sm:mx-auto sm:text-center lg:text-left lg:mx-0">
                <div className="sm:flex gap-3">
                  <Link
                    to="/register"
                    className="flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-500 md:py-4 md:text-lg transition-all shadow-lg shadow-primary-900/50"
                  >
                    Start Tracking
                  </Link>
                  <a
                    href="#how-it-works"
                    className="mt-3 sm:mt-0 flex items-center justify-center px-8 py-3 border border-gray-700 text-base font-medium rounded-lg text-primary-400 bg-gray-800 hover:bg-gray-700 md:py-4 md:text-lg transition-all"
                  >
                    How it works
                  </a>
                </div>
                <p className="mt-4 text-xs text-gray-500">
                  🔒 End-to-end encrypted. No tracking. No ads.
                </p>
              </div>
            </div>

            <div className="mt-12 relative sm:max-w-lg sm:mx-auto lg:mt-0 lg:max-w-none lg:mx-0 lg:col-span-6 lg:flex lg:items-center">
              <div className="relative mx-auto w-full rounded-2xl border border-gray-800 bg-gray-800/50 p-12 overflow-hidden shadow-2xl">
                <div className="absolute -top-24 -right-24 w-64 h-64 bg-primary-600/20 blur-3xl rounded-full"></div>
                <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-primary-900/30 blur-3xl rounded-full"></div>

                <div className="relative flex flex-col items-center justify-center text-center">
                  <div className="text-9xl mb-4 drop-shadow-2xl">🛡️</div>
                  <div className="text-6xl absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-20">
                    👶
                  </div>
                  <p className="text-primary-400 font-mono text-sm mt-4">
                    ENCRYPTION: AES-256-GCM ACTIVE
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section className="py-20 bg-gray-900 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-base text-primary-500 font-semibold tracking-wide uppercase">
              Comprehensive Features
            </h2>
            <p className="mt-2 text-3xl font-extrabold text-white sm:text-4xl">
              Everything you need, totally private.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gray-800/40 border border-gray-800 p-8 rounded-2xl">
              <div className="w-12 h-12 bg-pink-900/50 text-pink-400 rounded-lg flex items-center justify-center text-2xl mb-6">
                🤰
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Pregnancy Tracking</h3>
              <p className="text-gray-400">
                Log symptoms, track kicks, monitor blood pressure, and record appointments
                throughout your pregnancy. Weekly gestation tracker with progress bar and
                timeline view.
              </p>
            </div>
            <div className="bg-gray-800/40 border border-gray-800 p-8 rounded-2xl">
              <div className="w-12 h-12 bg-primary-900/50 text-primary-400 rounded-lg flex items-center justify-center text-2xl mb-6">
                🍼
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Feeding &amp; Growth</h3>
              <p className="text-gray-400">
                Track breastfeeding (L/R sides), bottle feeds, solids, and growth metrics like
                weight and height with beautiful charts.
              </p>
            </div>
            <div className="bg-gray-800/40 border border-gray-800 p-8 rounded-2xl">
              <div className="w-12 h-12 bg-primary-900/50 text-primary-400 rounded-lg flex items-center justify-center text-2xl mb-6">
                😴
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Sleep &amp; Routine</h3>
              <p className="text-gray-400">
                Log naps, overnight sleep, and nappy changes. Identify patterns in your baby's
                routine without data mining.
              </p>
            </div>
            <div className="bg-gray-800/40 border border-gray-800 p-8 rounded-2xl">
              <div className="w-12 h-12 bg-green-900/50 text-green-400 rounded-lg flex items-center justify-center text-2xl mb-6">
                🔄
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Seamless Conversion</h3>
              <p className="text-gray-400">
                When your baby arrives, convert your pregnancy profile to a baby profile with
                one tap. All your pregnancy history is preserved, and you continue tracking
                without missing a beat.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-gray-950 border-y border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
            <div>
              <h2 className="text-3xl font-extrabold text-white sm:text-4xl mb-6">
                Zero-Knowledge by Design
              </h2>
              <p className="text-lg text-gray-400 mb-8">
                Most apps say they are "secure," but their employees can still browse your baby's
                photos and logs. At myTotTot, we use <strong>Web Crypto API</strong> to encrypt
                your data before it ever leaves your browser.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="text-primary-500 mr-3">✔</span>
                  <span>
                    <strong>No Plaintext:</strong> Our database only stores gibberish.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-500 mr-3">✔</span>
                  <span>
                    <strong>Your Keys:</strong> Your password generates the decryption key.
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-500 mr-3">✔</span>
                  <span>
                    <strong>Secure Sharing:</strong> Use RSA-4096 keys to invite partners.
                  </span>
                </li>
              </ul>
            </div>
            <div className="mt-12 lg:mt-0 bg-gray-900 rounded-2xl p-1 border border-primary-500/30 shadow-2xl">
              <div className="bg-gray-800 rounded-xl p-6 font-mono text-xs text-primary-300">
                <p className="mb-2">// Client-Side Encryption Example</p>
                <p className="text-white">const encryptedData = await crypto.subtle.encrypt(</p>
                <p className="pl-4">{' { name: "AES-GCM", iv: iv },'}</p>
                <p className="pl-4 text-yellow-400"> babyKey, // Stored only in your memory</p>
                <p className="pl-4">{' encoder.encode("Baby\'s First Smile!")'}</p>
                <p className="">);</p>
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <p className="text-gray-500 italic">// Server receives:</p>
                  <p className="text-pink-400">"0x9f2a1b5c8e7d..."</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sharing Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-extrabold text-white mb-6">Family &amp; Doctor Access</h2>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto mb-12">
            Need to share pregnancy logs with your partner or midwife? Or baby logs with a
            pediatrician? Generate secure, time-limited access tokens that give them temporary
            decryption rights without compromising your master account.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="p-6 bg-gray-800/50 rounded-xl border border-gray-800 text-left">
              <h4 className="text-white font-bold mb-2">Partner Sync</h4>
              <p className="text-sm text-gray-400">
                Real-time sync between devices. When one parent logs a feed, the other sees it
                instantly.
              </p>
            </div>
            <div className="p-6 bg-gray-800/50 rounded-xl border border-gray-800 text-left">
              <h4 className="text-white font-bold mb-2">Clinician Portal</h4>
              <p className="text-sm text-gray-400">
                Generate a 24-hour read-only link for your midwife or pediatrician to view
                charts during an appointment.
              </p>
            </div>
            <div className="p-6 bg-gray-800/50 rounded-xl border border-gray-800 text-left">
              <h4 className="text-white font-bold mb-2">Pregnancy → Baby</h4>
              <p className="text-sm text-gray-400">
                When baby arrives, convert your profile in one tap. Your full pregnancy history
                stays with you — no app switch needed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl mb-6">
            Start your journey — from pregnancy to preschool.
          </h2>
          <p className="text-xl text-primary-100 mb-10">
            Track your pregnancy and your child's health without giving up your family's privacy.
          </p>
          <Link
            to="/register"
            className="inline-block bg-white text-primary-600 px-10 py-4 rounded-xl font-bold text-lg hover:bg-primary-50 transition-colors shadow-2xl"
          >
            Create Your Secure Account
          </Link>
          <p className="mt-6 text-primary-200 text-sm">No credit card required. Free forever.</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-950 py-12 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-2 mb-4 md:mb-0">
              <span className="text-xl">👶</span>
              <span className="font-bold text-white">myTotTot</span>
            </div>
            <div className="flex space-x-6 text-sm text-gray-500">
              <Link to="/privacy" className="hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="hover:text-white transition-colors">
                Terms of Service
              </Link>
              <a href="mailto:support@mytottot.app" className="hover:text-white transition-colors">
                Support
              </a>
              <a
                href="https://buymeacoffee.com/mearkats"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors"
              >
                Donate
              </a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-900 text-center text-xs text-gray-600">
            &copy; 2026 myTotTot. All pregnancy and baby data is end-to-end encrypted.
          </div>
        </div>
      </footer>
    </div>
  );
}
